import { Dayjs } from "dayjs";
import { useField, useFormikContext } from "formik";

import { Box, Typography } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, DatePickerProps } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

interface CustomDatePickerProps extends DatePickerProps<Dayjs> {
  name: string;
  label?: string;
  setOverlapError?: (e: string) => void;
}

const CustomDatePicker = ({
  name,
  label,
  setOverlapError,
  ...rest
}: CustomDatePickerProps) => {
  const [field, meta] = useField(name);
  const { setFieldTouched, setFieldValue } = useFormikContext();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {!!label && (
          <Typography
            sx={{
              color: "var(--neutral-1000)",
              font: { xs: "var(--body-xs)", md: "var(--body-sm)" },
              mb: "8px",
            }}
          >
            {label}
          </Typography>
        )}

        <DatePicker
          desktopModeMediaQuery="@media(min-width: 0px)" // forces desktop UI
          value={field.value}
          onChange={(newValue) => {
            setFieldValue(name, newValue);
            setFieldTouched(name, true, false);
            setOverlapError?.("");
          }}
          onClose={() => {
            setTimeout(() => {
              setFieldTouched(name, true, true);
            }, 0);
          }}
          slotProps={{
            textField: {
              required: true,
              error: meta.touched && !!meta.error,
              helperText: meta.touched && meta.error,
              fullWidth: true,
              placeholder: "dd/mm/yyyy",
              onBlur: () => setFieldTouched(name, true, true),
            },
            popper: {
              disablePortal: true, // Keeps popper inside modal DOM
              onClick: (e: any) => e.stopPropagation(), // Prevent modal close
              placement: "top-end",
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, -150],
                  },
                },
              ],
            },
          }}
          sx={{
            fontFamily: '"Lexend", sans-serif',
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "var(--neutral-50)",
              height: "44px",
              padding: "12px 14px",
              font: "var(--body-sm)",
              "& fieldset": {
                borderColor: "var(--neutral-200)",
              },
              "& placeholder": {
                color: "var(--neutral-400)",
              },
              "& .MuiInputBase-input": {
                padding: "0",
              },
            },
          }}
          {...rest}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default CustomDatePicker;
